import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Search, Loader2 } from "lucide-react";
import { useQuotations } from "@/hooks/useQuotations";
import { QuotationListItem, QuotationStatus } from "@/types/salesDocuments";
import { format } from "date-fns";
import { useDecimalPrecision } from "@/hooks/useDecimalPrecision";

interface QuotationPickerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectQuotation: (quotation: QuotationListItem) => void;
  excludeConverted?: boolean;
}

export const QuotationPickerModal = ({
  isOpen,
  onClose,
  onSelectQuotation,
  excludeConverted = true,
}: QuotationPickerModalProps) => {
  const { formatTotalAmount } = useDecimalPrecision();
  const [searchTerm, setSearchTerm] = useState("");

  const {
    data: quotationsData,
    isLoading,
    error,
  } = useQuotations({
    search: searchTerm,
  });

  // Filter out converted quotations if needed

  const filteredQuotations =
    quotationsData?.data?.filter((quotation: any) => {
      if (
        excludeConverted &&
        (quotation.isConverted || quotation.convertedToSalesOrder)
      ) {
        return false;
      }
      return true;
    }) || [];

  const getStatusColor = (status: QuotationStatus) => {
    const baseStyle = "rounded-full px-3 py-1 text-xs font-medium";
    switch (status) {
      case QuotationStatus.DRAFT:
        return `${baseStyle} bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300`;
      case QuotationStatus.SENT:
        return `${baseStyle} bg-blue-100 text-blue-800`;
      case QuotationStatus.ACCEPTED:
        return `${baseStyle} bg-green-100 text-green-800`;
      case QuotationStatus.REJECTED:
        return `${baseStyle} bg-red-100 text-red-800`;
      case QuotationStatus.EXPIRED:
        return `${baseStyle} bg-orange-100 text-orange-800`;
      case QuotationStatus.CONVERTED:
        return `${baseStyle} bg-purple-100 text-purple-800`;
      default:
        return `${baseStyle} bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300`;
    }
  };

  const handleSelect = (quotation: QuotationListItem) => {
    onSelectQuotation(quotation);
    onClose();
    setSearchTerm("");
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-6xl max-h-[80vh]">
        <DialogHeader>
          <DialogTitle>Select Quotation</DialogTitle>
          <DialogDescription>
            Search and select a quotation to create a sales order from.
            {excludeConverted &&
              " Only showing quotations that haven't been converted."}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="relative">
            <Search className="absolute left-2 top-2.5 h-4 w-4 text-gray-500" />
            <Input
              placeholder="Search by quotation number, buyer name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-8"
              aria-label="Search quotations"
            />
          </div>
          <div className="relative border rounded-md">
            <Table>
              <TableHeader className="bg-white dark:bg-gray-950 sticky top-0">
                <TableRow>
                  <TableHead className="w-[150px]">Quotation No.</TableHead>
                  <TableHead className="w-[200px]">Buyer Name</TableHead>
                  <TableHead className="w-[120px]">Date</TableHead>
                  <TableHead className="w-[120px]">Valid Until</TableHead>
                  <TableHead className="w-[120px]">Amount (RM)</TableHead>
                  <TableHead className="w-[100px]">Status</TableHead>
                  <TableHead className="w-[100px]"></TableHead>
                </TableRow>
              </TableHeader>
            </Table>
            <div className="max-h-[40vh] overflow-auto">
              <Table>
                <TableBody>
                  {isLoading ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8">
                        <Loader2 className="h-6 w-6 animate-spin mx-auto" />
                        <p className="mt-2 text-sm text-muted-foreground">
                          Loading quotations...
                        </p>
                      </TableCell>
                    </TableRow>
                  ) : error ? (
                    <TableRow>
                      <TableCell
                        colSpan={7}
                        className="text-center text-red-500 py-8"
                      >
                        Error loading quotations
                      </TableCell>
                    </TableRow>
                  ) : filteredQuotations.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8">
                        <p className="text-muted-foreground">
                          {searchTerm
                            ? "No quotations found matching your search"
                            : excludeConverted
                              ? "No available quotations to convert"
                              : "No quotations found"}
                        </p>
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredQuotations.map((quotation) => (
                      <TableRow key={quotation.id} className="hover:bg-muted">
                        <TableCell className="w-[150px] font-medium">
                          {quotation.codeNumber}
                        </TableCell>
                        <TableCell className="w-[200px]">
                          {quotation.buyerName || "N/A"}
                          <div className="text-xs text-gray-500">
                            {quotation.buyerCode}
                          </div>
                        </TableCell>
                        <TableCell className="w-[120px]">
                          {format(
                            new Date(quotation.quotationDate),
                            "dd/MM/yyyy",
                          )}
                        </TableCell>
                        <TableCell className="w-[120px]">
                          {format(new Date(quotation.validUntil), "dd/MM/yyyy")}
                        </TableCell>
                        <TableCell className="w-[120px] text-right">
                          {formatTotalAmount(quotation.totalPayableAmount)}
                        </TableCell>
                        <TableCell className="w-[100px]">
                          <Badge className={getStatusColor(quotation.status)}>
                            {quotation.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="w-[100px]">
                          <Button
                            size="sm"
                            onClick={() => handleSelect(quotation)}
                            disabled={
                              Boolean(
                                quotation.isConverted || quotation.salesOrderId,
                              ) && excludeConverted
                            }
                            aria-label={`Select quotation ${quotation.codeNumber}`}
                          >
                            Select
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default QuotationPickerModal;
